import Inspection from 'models/InspectionModel';
import InspectionTaskStatus from 'models/types/InspectionTaskStatus';
import React from 'react';
import { AiFillFileAdd } from 'react-icons/ai';
import { MdAddAPhoto, MdKeyboardArrowDown, MdVideocam } from 'react-icons/md';
import axios from 'helpers/axios';
import { Clipboard, Download, Edit3, Expand, Trash2 } from 'lucide-react';

import config from 'helpers/config';
import FullPhotoView, { PhotoFileModel } from 'components/common/FullPhotoView';
import SendInspectionModal from 'components/modals/SendInspectionModal';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import { FaCheck, FaExclamation } from 'react-icons/fa';
import { CgFileDocument } from 'react-icons/cg';

import moment from 'moment';
import dateFormats from 'helpers/dateFormats';

interface Props {
    inspection: Inspection;
    onInspectionUpdated: () => void;
    onInspectionCompleted: (completedInspection: Inspection) => void;
    onInspectionDeleted: () => void;
}

interface State {
    isPhotoExpanded: boolean;
    expandedPhoto?: any | null;
    expandedGroups: string[];
    photoFiles: PhotoFileModel[];
    editablePhotoTaskId?: string;
    initialPhotoIndex?: number;
    localInspection: Inspection; // Keep a local copy to manage state
    isSendModalOpen: boolean;
}

class InspectionCard extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isPhotoExpanded: false,
            expandedGroups: [],
            photoFiles: [],
            localInspection: props.inspection,
            isSendModalOpen: false,
        };
    }

    componentDidMount() {
        // expand all groups by default
        const expandedGroups = this.state.localInspection.groups.map((group) => group.name);
        this.setState({ expandedGroups });
    }

    componentDidUpdate(prevProps: Props) {
        // Update local inspection when parent updates
        if (prevProps.inspection !== this.props.inspection) {
            this.setState({ localInspection: this.props.inspection });
        }
    }

    // Handle rating updates internally
    updateRating = (rating: InspectionTaskStatus, groupIndex: number, taskIndex: number) => {
        const { localInspection } = this.state;
        if (localInspection.isCompleted) return;

        const updatedInspection = { ...localInspection };
        const currentRating = updatedInspection.groups[groupIndex].tasks[taskIndex].rating;

        // Toggle rating if same, otherwise set new rating
        updatedInspection.groups[groupIndex].tasks[taskIndex].rating = currentRating === rating ? null : rating;

        this.setState({ localInspection: updatedInspection }, () => {
            this.updateTask(groupIndex, taskIndex);
        });
    };

    // Handle task field changes
    handleTaskChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        groupIndex: number,
        taskIndex: number,
    ) => {
        const { name, value } = event.currentTarget;
        const { localInspection } = this.state;

        const updatedInspection = { ...localInspection };
        updatedInspection.groups[groupIndex].tasks[taskIndex] = {
            ...updatedInspection.groups[groupIndex].tasks[taskIndex],
            [name]: value,
        };

        this.setState({ localInspection: updatedInspection });
    };

    // Update task on server and refresh inspection
    updateTask = (groupIndex: number, taskIndex: number) => {
        const { localInspection } = this.state;
        const taskToUpdate = localInspection.groups[groupIndex].tasks[taskIndex];

        axios
            .put(`${config.apiUrl}/inspection/task`, { ...taskToUpdate })
            .then(() => {
                // Notify parent to refresh the inspection data
                this.props.onInspectionUpdated();
            })
            .catch((_) => console.log(_));
    };

    // Complete inspection
    completeInspection = () => {
        const { localInspection } = this.state;

        // Optimistically update local state
        this.setState({
            localInspection: { ...localInspection, isCompleted: true },
        });

        axios
            .put(`${config.apiUrl}/inspection/complete`, { inspectionId: localInspection.id })
            .then((res) => {
                this.props.onInspectionCompleted(res.data);
                // Prompt the advisor to send the finished inspection to the customer.
                this.setState({ isSendModalOpen: true });
            })
            .catch((_) => {
                // Revert optimistic update on error
                this.setState({ localInspection });
                console.log(_);
            });
    };

    // Delete inspection
    deleteInspection = () => {
        const { localInspection } = this.state;

        axios
            .delete(`${config.apiUrl}/inspection/${localInspection.id}`)
            .then(() => {
                this.props.onInspectionDeleted();
            })
            .catch((_) => console.log(_));
    };

    addPhoto(taskId: string, inspectionGroupIndex: number, taskIndex: number, isPhotoInput = true) {
        const imageInput = document.createElement('input');
        imageInput.type = 'file';
        imageInput.style.display = 'none';

        if (isPhotoInput) {
            imageInput.accept = '.gif,.jpg,.jpeg,.png';
        } else {
            imageInput.accept = '.xlsx,.xls,.doc,.docx,.ppt,.pptx,.txt,.pdf';
        }

        document.body.appendChild(imageInput);

        imageInput.onchange = (e) => {
            // @ts-ignore
            const file = e?.target?.files[0];

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = (_) => {
                const fileUploadRequest = [
                    {
                        dataURL: reader.result,
                        file: {
                            lastModified: file?.lastModified,
                            name: file?.name,
                            size: file?.size,
                        },
                        taskId,
                    },
                ];

                document.body.removeChild(imageInput);

                axios
                    .post(`${config.apiUrl}/file/${isPhotoInput ? 'images' : 'documents'}`, fileUploadRequest)
                    .then((_) => this.updateTask(inspectionGroupIndex, taskIndex));
            };
        };

        imageInput.click();
    }

    addVideo(taskId: string, inspectionGroupIndex: number, taskIndex: number) {
        const videoInput = document.createElement('input');
        videoInput.type = 'file';
        videoInput.accept = 'video/*';
        // Prefer the rear camera on mobile for capturing on the spot.
        videoInput.setAttribute('capture', 'environment');
        videoInput.style.display = 'none';
        document.body.appendChild(videoInput);

        videoInput.onchange = (e) => {
            // @ts-ignore
            const file = e?.target?.files[0];
            if (!file) {
                document.body.removeChild(videoInput);
                return;
            }
            // Stream the raw file as multipart/form-data (no base64 inflation, not
            // bound by the JSON body-size limit).
            const formData = new FormData();
            formData.append('video', file);
            formData.append('taskId', taskId);
            document.body.removeChild(videoInput);
            axios
                .post(`${config.apiUrl}/file/videos/upload`, formData)
                .then((_) => this.updateTask(inspectionGroupIndex, taskIndex))
                .catch((err) => console.log(err));
        };

        videoInput.click();
    }

    deleteInspectionFile = (
        fileId: string,
        inspectionGroupIndex: number,
        taskIndex: number,
        label: string,
    ) => {
        displayConfirmationModal(label)
            .then(() =>
                axios
                    .delete(`${config.apiUrl}/file/${fileId}`)
                    .then(() => this.updateTask(inspectionGroupIndex, taskIndex))
                    .catch((_) => console.log(_)),
            )
            .catch((_) => _);
    };

    viewFullPhoto(photos: any[], taskId?: string, photoIndex?: number) {
        this.setState({
            isPhotoExpanded: true,
            expandedPhoto: photos[0],
            photoFiles: photos.map((file) => ({
                id: file.id,
                fileName: file.fileName,
                photoSourceURL: `${config.fileRootURL}/images/${file.fileName}`,
            })),
            editablePhotoTaskId: taskId,
            initialPhotoIndex: photoIndex,
        });
    }

    getInspectionTaskStatusCount(taskStatusType: InspectionTaskStatus) {
        const { localInspection } = this.state;

        if (taskStatusType === InspectionTaskStatus.None) {
            return localInspection.groups?.reduce((acc, group) => {
                return acc + group.tasks?.filter((task) => !!!task.rating).length;
            }, 0);
        } else {
            return localInspection.groups?.reduce((acc, group) => {
                return acc + group.tasks?.filter((task) => task.rating === taskStatusType).length;
            }, 0);
        }
    }

    handleSaveAnnotation = (photoId: string, annotatedDataUrl: string) => {
        this.setState({ isPhotoExpanded: false, expandedPhoto: null, photoFiles: [] });

        return new Promise<void>((resolve, reject) => {
            // Detect the format from the data URL
            let fileExtension = 'jpg'; // Default to jpg for better compression
            if (annotatedDataUrl.startsWith('data:image/png')) {
                fileExtension = 'png';
            } else if (annotatedDataUrl.startsWith('data:image/webp')) {
                fileExtension = 'webp';
            }

            const fileUploadRequest = [
                {
                    dataURL: annotatedDataUrl,
                    file: {
                        lastModified: Date.now(),
                        name: `${photoId}-annotation.${fileExtension}`,
                        size: 0,
                    },
                    taskId: this.state.editablePhotoTaskId,
                    isAnnotatedImage: true,
                    originalImageId: photoId,
                },
            ];

            axios
                .post(`${config.apiUrl}/file/imageannotation`, fileUploadRequest)
                .then((_) => {
                    const inspectionGroupIndex = this.state.localInspection.groups.findIndex((group) =>
                        group.tasks.some((task) => task.id === this.state.editablePhotoTaskId),
                    );

                    const taskIndex = this.state.localInspection.groups[inspectionGroupIndex].tasks.findIndex(
                        (task) => task.id === this.state.editablePhotoTaskId,
                    );
                    this.updateTask(inspectionGroupIndex, taskIndex);
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    render() {
        const { localInspection } = this.state;
        const { isPhotoExpanded, expandedPhoto } = this.state;
        return (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm  flex flex-col justify-between hover:shadow-md transition-shadow duration-200 h-fit">
                {' '}
                {isPhotoExpanded && (
                    <FullPhotoView
                        photoFiles={this.state.photoFiles}
                        onClose={() => this.setState({ isPhotoExpanded: false, expandedPhoto: null })}
                        onSaveAnnotation={this.handleSaveAnnotation}
                        initialPhotoIndex={this.state.initialPhotoIndex}
                        outputQuality={0.85}
                        maxOutputSize={2048}
                    />
                )}
                <div className="flex justify-between items-center p-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">{localInspection.name}</h2>
                        <p className="text-sm text-gray-500">
                            Started at {moment(localInspection.createdAt).format(dateFormats.USA_DATETIME)} by{' '}
                            {localInspection?.createdBy?.firstName} {localInspection?.createdBy?.lastName}
                        </p>
                        {localInspection.isCompleted && (
                            <p className="text-sm text-gray-500">
                                Completed at {moment(localInspection.completionDate).format(dateFormats.USA_DATETIME)}{' '}
                                by {localInspection?.completedBy?.firstName} {localInspection?.completedBy?.lastName}
                            </p>
                        )}
                    </div>
                    <div className="flex space-x-2">
                        {!localInspection.isCompleted && (
                            <button className="p-1 text-blue-500 bg-blue-50 rounded-full">
                                <Clipboard size={16} />
                            </button>
                        )}
                        {!localInspection.isCompleted ? (
                            <div className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-md">In Progress</div>
                        ) : (
                            <div className="px-3 py-1 bg-green-100 text-green-600 text-sm rounded-md">Completed</div>
                        )}
                    </div>
                </div>
                {/* Status Summary */}
                <div className="grid grid-cols-4 border-b border-gray-100">
                    <div className="p-3 text-center border-r border-gray-100">
                        <div className="flex items-center justify-center bg-green-100 w-8 h-8 rounded-full mx-auto mb-1">
                            <span className="text-green-700 font-semibold">
                                {this.getInspectionTaskStatusCount(InspectionTaskStatus.OK)}
                            </span>
                        </div>
                        <p className="text-xs text-gray-700">Checked and OK</p>
                    </div>
                    <div className="p-3 text-center border-r border-gray-100">
                        <div className="flex items-center justify-center bg-yellow-100 w-8 h-8 rounded-full mx-auto mb-1">
                            <span className="text-yellow-700 font-semibold">
                                {this.getInspectionTaskStatusCount(InspectionTaskStatus.NeedsAttention)}
                            </span>
                        </div>
                        <p className="text-xs text-gray-700">May need attention</p>
                    </div>
                    <div className="p-3 text-center border-r border-gray-100">
                        <div className="flex items-center justify-center bg-red-100 w-8 h-8 rounded-full mx-auto mb-1">
                            <span className="text-red-700 font-semibold">
                                {this.getInspectionTaskStatusCount(InspectionTaskStatus.NeedsRepair)}
                            </span>
                        </div>
                        <p className="text-xs text-gray-700">Needs immediate attention</p>
                    </div>
                    <div className="p-3 text-center">
                        <div className="flex items-center justify-center bg-gray-100 w-8 h-8 rounded-full mx-auto mb-1">
                            <span className="text-gray-700 font-semibold">
                                {this.getInspectionTaskStatusCount(InspectionTaskStatus.None)}
                            </span>
                        </div>
                        <p className="text-xs text-gray-700">Not Inspected</p>
                    </div>
                </div>
                {!localInspection.isCompleted && (
                    <div className="flex border-b border-gray-100">
                        <button
                            className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
                            onClick={() => {
                                this.state.localInspection.groups?.forEach((group, groupIndex) => {
                                    group.tasks?.forEach((task, taskIndex) => {
                                        if (task.rating !== InspectionTaskStatus.OK) {
                                            this.updateRating(InspectionTaskStatus.OK, groupIndex, taskIndex);
                                        }
                                    });
                                });
                            }}
                        >
                            Pass All
                        </button>
                        <button
                            className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-colors"
                            onClick={() => {
                                this.state.localInspection.groups?.forEach((group, groupIndex) => {
                                    group.tasks?.forEach((task, taskIndex) => {
                                        if (task.rating !== InspectionTaskStatus.None) {
                                            this.updateRating(InspectionTaskStatus.None, groupIndex, taskIndex);
                                        }
                                    });
                                });
                            }}
                        >
                            Reset All
                        </button>
                    </div>
                )}
                <div className="divide-y-2 divide-gray-400">
                    {localInspection.groups.map((group, inspectionGroupIndex) => {
                        return (
                            <div key={localInspection.id + inspectionGroupIndex}>
                                <button
                                    //   onClick={() => toggleGroup('engine')}
                                    className="flex justify-between items-center w-full px-4 py-2 text-left bg-blue-100 hover:bg-blue-200"
                                    onClick={() => {
                                        const expandedGroups = new Set(this.state.expandedGroups);
                                        if (expandedGroups.has(group.name)) {
                                            expandedGroups.delete(group.name);
                                        } else {
                                            expandedGroups.add(group.name);
                                        }
                                        this.setState({ expandedGroups: Array.from(expandedGroups) });
                                    }}
                                >
                                    <h3 className="font-semibold text-lg text-gray-800">{group.name}</h3>

                                    <MdKeyboardArrowDown
                                        className={`transition-transform text-gray-500 hover:bg-blue-100 p-0.5 rounded-full ${
                                            this.state.expandedGroups.includes(group.name) ? 'rotate-180' : ''
                                        }`}
                                        size={20}
                                    />
                                </button>

                                {group.tasks.map((task, inspectionItemIndex) => {
                                    return (
                                        <div
                                            key={localInspection.id + inspectionItemIndex}
                                            className={`py-0 px-4 ${inspectionItemIndex === 0 ? 'pt-2' : ''}`}
                                            style={{
                                                display: this.state.expandedGroups.includes(group.name)
                                                    ? 'block'
                                                    : 'none',
                                            }}
                                        >
                                            <div className="font-semibold">
                                                #{inspectionItemIndex + 1} {task.description}
                                            </div>
                                            <div className="flex">
                                                <textarea
                                                    name="finding"
                                                    className="bg-white rounded-md border border-gray-300 w-3/5 px-2 font-medium py-2 focus:ring-1 focus:outline-none focus:border-blue-500"
                                                    value={task.finding}
                                                    onChange={(e) =>
                                                        this.handleTaskChange(
                                                            e,
                                                            inspectionGroupIndex,
                                                            inspectionItemIndex,
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        this.updateTask(inspectionGroupIndex, inspectionItemIndex)
                                                    }
                                                    placeholder="Enter finding"
                                                    disabled={localInspection.isCompleted}
                                                ></textarea>
                                                <div className="ml-5 pt-4 opacity-75">
                                                    <div
                                                        className={`rounded-full text-sm border border-green-600 inline-block mr-2 align-middle  ${
                                                            task.rating === InspectionTaskStatus.OK && 'bg-green-600'
                                                        } ${
                                                            localInspection.isCompleted
                                                                ? 'cursor-not-allowed'
                                                                : 'clickable'
                                                        }`}
                                                        onClick={() =>
                                                            this.updateRating(
                                                                InspectionTaskStatus.OK,
                                                                inspectionGroupIndex,
                                                                inspectionItemIndex,
                                                            )
                                                        }
                                                    >
                                                        <FaCheck size={24} className="text-white m-1" />
                                                    </div>
                                                    <div
                                                        className={`rounded-full text-sm border border-yellow-600 inline-block mr-2 align-middle  ${
                                                            task.rating === InspectionTaskStatus.NeedsAttention &&
                                                            'bg-yellow-600'
                                                        } ${
                                                            localInspection.isCompleted
                                                                ? 'cursor-not-allowed'
                                                                : 'clickable'
                                                        }`}
                                                        onClick={() =>
                                                            this.updateRating(
                                                                InspectionTaskStatus.NeedsAttention,
                                                                inspectionGroupIndex,
                                                                inspectionItemIndex,
                                                            )
                                                        }
                                                    >
                                                        <FaExclamation size={24} className="text-white m-1" />
                                                    </div>
                                                    <div
                                                        className={`rounded-full text-sm border border-red-600 inline-block mr-2 align-middle  ${
                                                            task.rating === InspectionTaskStatus.NeedsRepair &&
                                                            'bg-red-600'
                                                        } ${
                                                            localInspection.isCompleted
                                                                ? 'cursor-not-allowed'
                                                                : 'clickable'
                                                        }`}
                                                        onClick={() =>
                                                            this.updateRating(
                                                                InspectionTaskStatus.NeedsRepair,
                                                                inspectionGroupIndex,
                                                                inspectionItemIndex,
                                                            )
                                                        }
                                                    >
                                                        <FaExclamation size={24} className="text-white m-1" />
                                                    </div>
                                                </div>

                                                {!localInspection.isCompleted && (
                                                    <div className="ml-auto text-2xl pt-4">
                                                        <AiFillFileAdd
                                                            className="clickable inline"
                                                            onClick={() =>
                                                                this.addPhoto(
                                                                    task.id,
                                                                    inspectionGroupIndex,
                                                                    inspectionItemIndex,
                                                                    false,
                                                                )
                                                            }
                                                        >
                                                            <input
                                                                type="file"
                                                                alt="Submit"
                                                                width="48"
                                                                height="48"
                                                            ></input>
                                                        </AiFillFileAdd>
                                                        <MdAddAPhoto
                                                            className="clickable inline"
                                                            onClick={() =>
                                                                this.addPhoto(
                                                                    task.id,
                                                                    inspectionGroupIndex,
                                                                    inspectionItemIndex,
                                                                )
                                                            }
                                                        >
                                                            <input
                                                                type="image"
                                                                alt="Submit"
                                                                width="48"
                                                                height="48"
                                                            ></input>
                                                        </MdAddAPhoto>
                                                        <MdVideocam
                                                            className="clickable inline ml-1"
                                                            title="Add video"
                                                            onClick={() =>
                                                                this.addVideo(
                                                                    task.id,
                                                                    inspectionGroupIndex,
                                                                    inspectionItemIndex,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {task.files
                                                    ?.sort((a, b) => {
                                                        return new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1;
                                                    })
                                                    .filter((file) => file.type === 'image')
                                                    .map((file, photoIndex) => {
                                                        return (
                                                            <div key={file.id}>
                                                                <div
                                                                    className="relative group cursor-pointer rounded-lg overflow-hidden border-2 border-gray-200"
                                                                    onClick={() =>
                                                                        this.viewFullPhoto(
                                                                            task?.files.filter(
                                                                                (f) => f.type === 'image',
                                                                            ),
                                                                            task.id,
                                                                            photoIndex,
                                                                        )
                                                                    }
                                                                >
                                                                    <img
                                                                        src={`${config.fileRootURL}/images/${file.fileName}`}
                                                                        alt={file.fileName}
                                                                        title={file.fileName}
                                                                        className="clickable border-2 rounded-lg inspection-item-photo-tooltip inline "
                                                                    />

                                                                    <div
                                                                        className={`absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 opacity-0 group-hover:opacity-100 transition-all duration-200`}
                                                                    >
                                                                        {/* Top row icons */}
                                                                        <div className="absolute top-2 left-2 right-2 flex justify-between">
                                                                            {!localInspection.isCompleted && (
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        this.deleteInspectionFile(
                                                                                            file.id,
                                                                                            inspectionGroupIndex,
                                                                                            inspectionItemIndex,
                                                                                            'this image',
                                                                                        );
                                                                                    }}
                                                                                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                                                                                    title="Delete image"
                                                                                >
                                                                                    <Trash2 size={14} />
                                                                                </button>
                                                                            )}

                                                                            <button
                                                                                onClick={(e) => {
                                                                                    // e.stopPropagation();
                                                                                    // onAction('fullscreen', image.id, itemId);
                                                                                }}
                                                                                className="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors"
                                                                                title="View fullscreen"
                                                                            >
                                                                                <Expand size={14} />
                                                                            </button>
                                                                        </div>

                                                                        {/* Bottom row icons */}
                                                                        <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    // e.stopPropagation();
                                                                                    // onAction('annotate', image.id, itemId);
                                                                                }}
                                                                                className="w-8 h-8 bg-purple-500 hover:bg-purple-600 text-white rounded-full flex items-center justify-center transition-colors"
                                                                                title="Add annotations"
                                                                            >
                                                                                <Edit3 size={14} />
                                                                            </button>

                                                                            {/* Download/View original */}
                                                                            <button
                                                                                className="w-8 h-8 bg-gray-600 hover:bg-gray-700 text-white rounded-full flex items-center justify-center transition-colors"
                                                                                title="Download image"
                                                                            >
                                                                                <Download size={14} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {task.files
                                                    ?.filter((file) => file.type === 'video')
                                                    .map((file) => (
                                                        <div key={file.id} className="relative">
                                                            <video
                                                                src={`${config.fileRootURL}/videos/${file.fileName}`}
                                                                controls
                                                                className="w-96 h-56 rounded-lg border-2 border-gray-200 bg-black"
                                                            />
                                                            {!localInspection.isCompleted && (
                                                                <button
                                                                    onClick={() =>
                                                                        this.deleteInspectionFile(
                                                                            file.id,
                                                                            inspectionGroupIndex,
                                                                            inspectionItemIndex,
                                                                            'this video',
                                                                        )
                                                                    }
                                                                    className="absolute top-1 right-1 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
                                                                    title="Delete video"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                            </div>
                                            <div className="flex flex-col my-2">
                                                {task.files
                                                    ?.sort((a, b) => {
                                                        return new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1;
                                                    })
                                                    .map((file) => {
                                                        if (file.type === 'document') {
                                                            return (
                                                                <div key={file.id} className="flex mt-1">
                                                                    <div className="truncate">
                                                                        <CgFileDocument className="inline mr-2" />
                                                                        <a
                                                                            className="text-blue-500 font-bold text-sm hover:underline clickable whitespace-nowrap truncate"
                                                                            href={`${config.fileRootURL}/documents/${file.fileName}`}
                                                                            download={`${file.fileName}`}
                                                                            rel="noreferrer"
                                                                            title={file.fileName}
                                                                            target="_blank"
                                                                        >
                                                                            {file.fileName}
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    <div className="flex pt-2 px-4 py-2 bg-gray-200">
                        <div>
                            <button
                                className="px-4 py-2  border-red-600 text-red-500 font-semibold  rounded-md hover:text-red-700 hover:underline"
                                onClick={this.deleteInspection}
                            >
                                Delete
                            </button>
                        </div>
                        <div className="ml-auto">
                            {localInspection.isCompleted ? (
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold transition-colors"
                                    onClick={() => this.setState({ isSendModalOpen: true })}
                                >
                                    Send to customer
                                </button>
                            ) : (
                                <button
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-semibold transition-colors"
                                    onClick={this.completeInspection}
                                >
                                    Complete
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {this.state.isSendModalOpen && (
                    <SendInspectionModal
                        inspectionId={localInspection.id}
                        onClose={() => this.setState({ isSendModalOpen: false })}
                    />
                )}
            </div>
        );
    }
}

export default InspectionCard;
